import React, { useState } from 'react';
import { Phone, Mail, MapPin, Building, User, Calendar, DollarSign, Tag } from 'lucide-react';
import InlineEditField from '../shared/InlineEditField';
import StatusBadge from '../shared/StatusBadge';
import { updateLead, getCustomField, setCustomField } from '../../services/perfex-api';

/**
 * Lead Details Panel Component
 * Shows detailed lead information with inline editing including custom fields
 *
 * @param {Object} lead - Selected lead object
 * @param {function} onUpdate - Callback when lead is updated (updatedLead) => void
 * @param {function} onCallClick - Callback when call button is clicked
 */
const LeadDetailsPanel = ({ lead, onUpdate, onCallClick }) => {
    const [localLead, setLocalLead] = useState(lead);

    if (!lead) {
        return (
            <div className="flex items-center justify-center h-full bg-gray-50">
                <div className="text-center text-gray-500">
                    <User size={48} className="mx-auto mb-3 opacity-30" />
                    <p>Select a lead to view details</p>
                </div>
            </div>
        );
    }

    // Handle standard field update
    const handleFieldUpdate = async (fieldName, value) => {
        try {
            const updateData = { [fieldName]: value };
            const result = await updateLead(lead.id, updateData);

            if (result.success) {
                const updatedLead = { ...localLead, [fieldName]: value };
                setLocalLead(updatedLead);
                onUpdate(updatedLead);
                return Promise.resolve();
            } else {
                throw new Error(result.data?.message || 'Failed to update lead');
            }
        } catch (error) {
            console.error('Error updating lead:', error);
            throw error;
        }
    };

    // Handle custom field update
    const handleCustomFieldUpdate = async (fieldLabel, value) => {
        try {
            // Update custom field in lead object
            const updatedLead = setCustomField(localLead, fieldLabel, value);

            // Send update to API
            const result = await updateLead(lead.id, {
                customfields: updatedLead.customfields
            });

            if (result.success) {
                setLocalLead(updatedLead);
                onUpdate(updatedLead);
                return Promise.resolve();
            } else {
                throw new Error(result.data?.message || 'Failed to update custom field');
            }
        } catch (error) {
            console.error('Error updating custom field:', error);
            throw error;
        }
    };

    // Status options (these should match your Perfex CRM statuses)
    const statusOptions = [
        { value: '1', label: 'Customer' },
        { value: '2', label: 'Contacted' },
        { value: '3', label: 'Qualified' },
        { value: '4', label: 'Unqualified' },
        { value: '5', label: 'Lost' },
        { value: '6', label: 'Junk' }
    ];

    // Source options
    const sourceOptions = [
        { value: '1', label: 'Google' },
        { value: '2', label: 'Facebook' },
        { value: '3', label: 'Website' },
        { value: '4', label: 'Referral' },
        { value: '5', label: 'Other' }
    ];

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Header with Call Button */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            {localLead.name || 'Unnamed Lead'}
                        </h2>
                        {localLead.company && (
                            <p className="text-gray-600 flex items-center gap-2">
                                <Building size={16} />
                                {localLead.company}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <StatusBadge
                        status={localLead.status_name || 'Unknown'}
                        color={localLead.color}
                    />
                    {localLead.source_name && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                            {localLead.source_name}
                        </span>
                    )}
                    <span className="text-sm text-gray-600">Lead #{localLead.id}</span>
                </div>

                {/* Call Button */}
                <button
                    onClick={onCallClick}
                    className="w-full py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition-colors shadow-lg flex items-center justify-center gap-3"
                >
                    <Phone size={24} />
                    Call Lead - View Script
                </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Basic Information */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                        Contact Information
                    </h3>

                    {/* Name */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <User size={16} />
                            Name
                        </label>
                        <InlineEditField
                            value={localLead.name || ''}
                            onSave={(value) => handleFieldUpdate('name', value)}
                            placeholder="Enter name"
                        />
                    </div>

                    {/* Company */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <Building size={16} />
                            Company
                        </label>
                        <InlineEditField
                            value={localLead.company || ''}
                            onSave={(value) => handleFieldUpdate('company', value)}
                            placeholder="Enter company name"
                        />
                    </div>

                    {/* Email */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <Mail size={16} />
                            Email
                        </label>
                        <InlineEditField
                            value={localLead.email || ''}
                            onSave={(value) => handleFieldUpdate('email', value)}
                            type="email"
                            placeholder="Enter email"
                        />
                    </div>

                    {/* Phone */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <Phone size={16} />
                            Phone Number
                        </label>
                        <InlineEditField
                            value={localLead.phonenumber || ''}
                            onSave={(value) => handleFieldUpdate('phonenumber', value)}
                            type="tel"
                            placeholder="Enter phone number"
                        />
                    </div>
                </div>

                {/* Address Information */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                        Address
                    </h3>

                    {/* Full Address */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <MapPin size={16} />
                            Street Address
                        </label>
                        <InlineEditField
                            value={localLead.address || ''}
                            onSave={(value) => handleFieldUpdate('address', value)}
                            placeholder="Enter street address"
                        />
                    </div>

                    {/* City, State, ZIP in a grid */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-2 block">
                                City
                            </label>
                            <InlineEditField
                                value={localLead.city || ''}
                                onSave={(value) => handleFieldUpdate('city', value)}
                                placeholder="City"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-2 block">
                                State
                            </label>
                            <InlineEditField
                                value={localLead.state || ''}
                                onSave={(value) => handleFieldUpdate('state', value)}
                                placeholder="State"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-2 block">
                                ZIP Code
                            </label>
                            <InlineEditField
                                value={localLead.zip || ''}
                                onSave={(value) => handleFieldUpdate('zip', value)}
                                placeholder="ZIP"
                            />
                        </div>
                    </div>
                </div>

                {/* Lead Status */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                        Lead Status
                    </h3>

                    {/* Status */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <Tag size={16} />
                            Status
                        </label>
                        <InlineEditField
                            value={String(localLead.status || '1')}
                            onSave={(value) => handleFieldUpdate('status', value)}
                            type="select"
                            options={statusOptions}
                        />
                    </div>

                    {/* Source */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            Source
                        </label>
                        <InlineEditField
                            value={String(localLead.source || '1')}
                            onSave={(value) => handleFieldUpdate('source', value)}
                            type="select"
                            options={sourceOptions}
                        />
                    </div>
                </div>

                {/* Custom Fields - GreenTech Shredding Specific */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                        Service Details
                    </h3>

                    {/* Boxes */}
                    <div className="mb-4">
                        <label className="text-sm font-semibold text-gray-700 mb-2 block">
                            Boxes
                        </label>
                        <InlineEditField
                            value={getCustomField(localLead, 'Boxes') || ''}
                            onSave={(value) => handleCustomFieldUpdate('Boxes', value)}
                            placeholder="Enter number of boxes"
                        />
                    </div>

                    {/* Date of Service */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <Calendar size={16} />
                            Date of Service
                        </label>
                        <InlineEditField
                            value={getCustomField(localLead, 'Date of Service') || ''}
                            onSave={(value) => handleCustomFieldUpdate('Date of Service', value)}
                            type="date"
                            placeholder="Select service date"
                        />
                    </div>

                    {/* Quoted Amount */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <DollarSign size={16} />
                            Quoted Amount
                        </label>
                        <InlineEditField
                            value={getCustomField(localLead, 'Quoted') || ''}
                            onSave={(value) => handleCustomFieldUpdate('Quoted', value)}
                            placeholder="Enter quoted amount"
                        />
                    </div>

                    {/* Material Type */}
                    <div className="mb-4">
                        <label className="text-sm font-semibold text-gray-700 mb-2 block">
                            Material Type
                        </label>
                        <InlineEditField
                            value={getCustomField(localLead, 'Material Type') || ''}
                            onSave={(value) => handleCustomFieldUpdate('Material Type', value)}
                            placeholder="e.g., Paper, Cardboard, Hard Drives"
                        />
                    </div>

                    {/* Frequency */}
                    <div className="mb-4">
                        <label className="text-sm font-semibold text-gray-700 mb-2 block">
                            Frequency
                        </label>
                        <InlineEditField
                            value={getCustomField(localLead, 'Frequency') || ''}
                            onSave={(value) => handleCustomFieldUpdate('Frequency', value)}
                            placeholder="e.g., Weekly, Monthly, One-time"
                        />
                    </div>

                    {/* Number of Boxes */}
                    <div className="mb-4">
                        <label className="text-sm font-semibold text-gray-700 mb-2 block">
                            Number of Boxes
                        </label>
                        <InlineEditField
                            value={getCustomField(localLead, 'Number of Boxes') || ''}
                            onSave={(value) => handleCustomFieldUpdate('Number of Boxes', value)}
                            placeholder="Enter number"
                        />
                    </div>
                </div>

                {/* Description */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                        Notes
                    </h3>
                    <InlineEditField
                        value={localLead.description || ''}
                        onSave={(value) => handleFieldUpdate('description', value)}
                        multiline={true}
                        placeholder="Add notes about this lead..."
                    />
                </div>

                {/* Additional Info */}
                <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        Additional Information
                    </h3>
                    <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                            <span>Added:</span>
                            <span className="font-medium">
                                {localLead.dateadded
                                    ? new Date(localLead.dateadded).toLocaleDateString()
                                    : 'N/A'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>Last Contact:</span>
                            <span className="font-medium">
                                {localLead.lastcontact
                                    ? new Date(localLead.lastcontact).toLocaleDateString()
                                    : 'N/A'}
                            </span>
                        </div>
                        {localLead.date_converted && (
                            <div className="flex justify-between">
                                <span>Converted:</span>
                                <span className="font-medium">
                                    {new Date(localLead.date_converted).toLocaleDateString()}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeadDetailsPanel;
